import { Flex, Form, Typography } from "antd";
import { FORM_VALIDATION_DEBOUNCE } from "constants/form";
import { BID_DRAFT_ACTION_MODE } from "interfaces";
import { observer } from "mobx-react-lite";
import { useNotificationContext } from "providers";
import { useMemo, useState } from "react";

import { declineDraftBid, returnBidToDraft } from "services";
import { useStores } from "stores";
import { CustomButton, DefaultTextArea } from "ui";
import { requestWithNotify } from "utils";
import { _isNotLongerThan, _isRequired, _isValidCommentText } from "utils/form/simple-validation";

interface BidDraftActionModalProps {
  mode: BID_DRAFT_ACTION_MODE;
}

export const BidDraftActionModal = observer(({ mode }: BidDraftActionModalProps) => {
  const { actionModals, bidCard } = useStores();
  const { openNotification } = useNotificationContext();

  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const comment = Form.useWatch("comment", form);

  const isDeclineMode = mode === BID_DRAFT_ACTION_MODE.DECLINE;

  const disabled = useMemo(() => {
    if (!isDeclineMode) {
      return loading;
    }

    return loading || !String(comment || "").trim();
  }, [loading, comment, isDeclineMode]);

  const closeModal = () => {
    actionModals.closeAll();
  };

  const onFinish = async () => {
    setLoading(true);

    const formattedComment = String(comment || "").trim();

    try {
      const res = await requestWithNotify({
        fetchFn: () =>
          isDeclineMode
            ? declineDraftBid({ id: bidCard.bid.id }, { comment: formattedComment })
            : returnBidToDraft({ id: bidCard.bid.id }),
        openNotification,
        successMsg: isDeclineMode ? "Черновик заявки отклонен" : "Заявка успешно возвращена на рассмотрение",
      });

      if (!res.result) {
        return;
      }

      await requestWithNotify({
        fetchFn: () => bidCard.fetchCardById(bidCard.bid.id),
        openNotification,
        showErrorNotification: false,
        delay: 1000,
      });

      closeModal();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex vertical gap="large">
      <Form form={form} name={`bidDraftAction-${mode}`} onFinish={onFinish}>
        <Flex vertical gap="small">
          {isDeclineMode ? (
            <Form.Item
              name="comment"
              rules={[
                _isRequired("Укажите причину отклонения"),
                _isNotLongerThan(150, "Причина отклонения не должна быть длиннее 150 символов"),
                _isValidCommentText("Причина отклонения не должна содержать специальные символы"),
              ]}
              validateDebounce={FORM_VALIDATION_DEBOUNCE}
            >
              <DefaultTextArea placeholder="Причина отклонения..." maxLength={150} rows={4} disabled={loading} />
            </Form.Item>
          ) : (
            <Typography.Text className="margin-bottom-lg">
              Вы уверены, что хотите вернуть заявку на рассмотрение?
            </Typography.Text>
          )}
        </Flex>

        <Form.Item className="margin-none">
          <Flex gap="large">
            <CustomButton htmlType="submit" disabled={disabled} loading={loading} className="full-width">
              Подтвердить
            </CustomButton>
            <CustomButton
              htmlType="button"
              type="default"
              disabled={loading}
              loading={loading}
              onClick={closeModal}
              className="full-width"
            >
              Отмена
            </CustomButton>
          </Flex>
        </Form.Item>
      </Form>
    </Flex>
  );
});
